import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseFloatingOptions {
    getAnchor: () => HTMLElement | null;
    menuWidth: number; // in px
    offset?: number; // gap between anchor and menu
    align?: 'start' | 'end';
}

export function useFloatingPosition({ getAnchor, menuWidth, offset = 8, align = 'end' }: UseFloatingOptions) {
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const update = useCallback(() => {
        const anchor = getAnchor();
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();

        // For position: fixed, rect is relative to viewport; do NOT add window.scroll offsets
        const viewportPadding = 8;
        const menuHeightApprox = 200; // heuristic; vertical flip rarely needed for small menus

        let top = rect.bottom + offset;
        if (top + menuHeightApprox > window.innerHeight - viewportPadding) {
            // Flip above if not enough space below
            top = Math.max(viewportPadding, rect.top - offset - menuHeightApprox);
        }

        let left: number;
        if (align === 'end') {
            left = rect.right - menuWidth;
        } else {
            left = rect.left;
        }

        // Horizontal shift within viewport
        if (left + menuWidth > window.innerWidth - viewportPadding) {
            left = window.innerWidth - viewportPadding - menuWidth;
        }
        if (left < viewportPadding) {
            left = viewportPadding;
        }

        setCoords({ top, left });
    }, [getAnchor, menuWidth, offset, align]);

    useEffect(() => {
        update();
        const onScroll = () => update();
        const onResize = () => update();
        // capture true to catch scroll in nested containers
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [update]);

    const style = useMemo(() => ({ position: 'fixed' as const, top: coords.top, left: coords.left }), [coords]);

    return { style, update };
}






